import { Badge } from '@components/badge';
import { Button } from '@components/button';
import {
  RequestDetailsSections,
  RequestDetailsUrlRow,
} from '@components/integrations/request-details';
import { hideModal, Modal, useModal } from '@components/modal';
import { Stack } from '@components/stack';
import { Text } from '@components/text';
import { useScopedTranslate } from '@locale/translate';
import { abbreviateDate } from '@utils/date';

import { useTravellerActionIntegrationResponses } from 'api/traveller-actions/hooks';
import { TravellerActionIntegrationResponse } from 'api/traveller-actions/requests';

import { getStatusCodeBadgeVariant } from './status-code-variant';

export const useRequestDetailModal = () => useModal(RequestDetailModal, { variant: 'LARGE' });

type RequestDetailModalProps = {
  actionId: string;
  responseShortId: string;
  initialResponse: TravellerActionIntegrationResponse;
};

const RequestDetailModal = ({
  actionId,
  responseShortId,
  initialResponse,
}: RequestDetailModalProps) => {
  const { t } = useScopedTranslate(locale);
  const { data: responses } = useTravellerActionIntegrationResponses(actionId);
  const response =
    responses?.find((candidate) => candidate.shortId === responseShortId) ?? initialResponse;
  const requestTypeLabel = (response.requestType ?? '').toUpperCase() || t('unknownType');

  return (
    <Modal>
      <Modal.SimpleHeader title={t('title')} />
      <Modal.Content>
        <Stack space={4}>
          <Stack space={2} orientation="horizontal" align="center">
            <Badge
              variant={getStatusCodeBadgeVariant(response.statusCode)}
              text={response.statusCode ?? t('noStatus')}
            />
            <Text font="regular14" color="textHelper" text={requestTypeLabel} />
            <Text
              font="regular14"
              color="textHelper"
              text={abbreviateDate(response.dateCreated, 'en', 'datetime-seconds')}
            />
          </Stack>
          <RequestDetailsUrlRow url={response.url} />
          <RequestDetailsSections data={response} />
        </Stack>
      </Modal.Content>
      <Modal.Footer>
        <Stack space={4} orientation="horizontal" justify="flex-end">
          <Button variant="secondary" onClick={hideModal} text={t('core.close')} />
        </Stack>
      </Modal.Footer>
    </Modal>
  );
};

const locale = {
  en: {
    title: 'Request details',
    noStatus: 'No status',
    unknownType: 'Unknown',
  },
};
